// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-06-19
// protected: NO  -> fix routing loop: navigasi pakai path ABSOLUT /v2/* (bukan relatif).
// =============================================================================
// Entry V2 (/v2/*): auth gate (login Google) + routing role-based di dalam shell.
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { V2AuthProvider, useV2Auth } from "@/v2/V2AuthContext";
import { V2RealtimeProvider } from "@/v2/V2RealtimeContext";
import { V2Shell } from "@/v2/V2Shell";
import { BrandLogo, BrandMark } from "@/v2/brand";
import { MENU } from "@/v2/menu";
import { Placeholder } from "@/v2/pages/Placeholder";
import Dashboard from "@/v2/pages/Dashboard";
import WorkOrderPage from "@/v2/pages/WorkOrderPage";
import MonitoringPage from "@/v2/pages/MonitoringPage";
import SOPPage from "@/v2/pages/SOPPage";
import GudangPage from "@/v2/pages/GudangPage";
import TransferPage from "@/v2/pages/TransferPage";
import PengirimanPage from "@/v2/pages/PengirimanPage";
import ReturPage from "@/v2/pages/ReturPage";
import PenjualanPage from "@/v2/pages/PenjualanPage";
import InvoicePage from "@/v2/pages/InvoicePage";
import OpnamePage from "@/v2/pages/OpnamePage";
import SaldoAwalPage from "@/v2/pages/SaldoAwalPage";
import StockAlertPage from "@/v2/pages/StockAlertPage";
import MutasiPage from "@/v2/pages/MutasiPage";
import MerchandisePage from "@/v2/pages/MerchandisePage";
import DiscountsPage from "@/v2/pages/DiscountsPage";
import ComplimentPage from "@/v2/pages/ComplimentPage";
import ArsipPage from "@/v2/pages/ArsipPage";
import UsersPage from "@/v2/pages/RoleMatrixPage";
import UatResetPage from "@/v2/pages/UatResetPage";
import StoreTasksPage from "@/v2/pages/StoreTasksPage";
import PermintaanPage from "@/v2/pages/PermintaanPage";
import PenagihanPage from "@/v2/pages/PenagihanPage";
import LaporanPage from "@/v2/pages/LaporanPage";
import QrTrackingPage from "@/v2/pages/QrTrackingPage";
import TukarPage from "@/v2/pages/TukarPage";
import PiutangPage from "@/v2/pages/PiutangPage";
import BatalkanProduksiPage from "@/v2/pages/BatalkanProduksiPage";
import BatalkanTransaksiPage from "@/v2/pages/BatalkanTransaksiPage";

// D-026 fix: Error Boundary ringan — bila sebuah halaman crash saat render,
// tampilkan pesan error (bukan blank putih) & shell/sidebar tetap berfungsi.
class PageBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null }); }
  render() {
    if (this.state.error) {
      return (
        <Card className="p-8 max-w-xl mx-auto mt-10 text-center space-y-4 rounded-2xl" data-testid="page-error-boundary">
          <div className="flex justify-center"><AlertTriangle className="w-10 h-10 text-destructive" /></div>
          <h2 className="text-xl font-semibold">Terjadi kesalahan saat menampilkan halaman</h2>
          <p className="text-sm text-muted-foreground break-words">{String(this.state.error?.message || this.state.error)}</p>
          <Button onClick={() => window.location.reload()} data-testid="page-error-reload">Muat ulang halaman</Button>
        </Card>
      );
    }
    return this.props.children;
  }
}


const READY = {
  dashboard: Dashboard,
  "work-orders": WorkOrderPage,
  permintaan: PermintaanPage,
  penagihan: PenagihanPage,
  monitoring: MonitoringPage,
  sop: SOPPage,
  gudang: GudangPage,
  transfer: TransferPage,
  pengiriman: PengirimanPage,
  retur: ReturPage,
  penjualan: PenjualanPage,
  invoice: InvoicePage,
  opname: OpnamePage,
  "saldo-awal": SaldoAwalPage,
  "stock-alerts": StockAlertPage,
  "laporan-mutasi": MutasiPage,
  laporan: LaporanPage,
  "qr-tracking": QrTrackingPage,
  merchandise: MerchandisePage,
  diskon: DiscountsPage,
  compliment: ComplimentPage,
  arsip: ArsipPage,
  users: UsersPage,
  "uat-reset": UatResetPage,
  "store-tasks": StoreTasksPage,
  tukar: TukarPage,
  piutang: PiutangPage,
  "batalkan-produksi": BatalkanProduksiPage,
  "batalkan-transaksi": BatalkanTransaksiPage,
};

const RoleGuard = ({ roles, role, children }) =>
  roles.includes(role) ? children : <Navigate to="/dashboard" replace />;

// Logo klub kiri (Arema/Borneo) — object-contain agar tidak gepeng; sembunyi bila gagal load.
const SideLogo = ({ src, alt, testid }) => (
  <img src={src} alt={alt} data-testid={testid}
       onError={(e) => { e.currentTarget.style.display = "none"; }}
       className="h-11 w-11 sm:h-14 sm:w-14 object-contain drop-shadow-lg" />
);

// Ikon Google resmi (multi-warna) untuk tombol login.
// KEKECUALIAN DISENGAJA (SLICE S-H/S-I B2): 4 hex di bawah adalah warna resmi brand
// Google "G" logo (wajib persis, bukan token tema kita) — sama kategorinya dengan
// hitam/putih paksa di .qr-print (index.css). BUKAN warna semantik aplikasi, jadi
// TIDAK ditukar ke token --badge-*/--warning/dst.
const GoogleIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001 6.19 5.238 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

// Tombol "Login dengan Google" — warna SELALU dari token --login-btn* (lihat index.css),
// TIDAK PERNAH ikut tema. Hover dikontrol lewat state React (bukan CSS :hover class) karena
// shadcn <Button> variant="default" membawa kelas Tailwind "hover:bg-primary/90" bawaan yang
// berebut prioritas cascade-layer dgn kelas CSS biasa pada spesifisitas yang sama — inline
// style (prioritas tertinggi, di luar !important) menjamin latar & teks SELALU berpasangan
// benar di kondisi normal maupun hover, tanpa terpengaruh urutan/layer stylesheet apa pun.
const GoogleLoginButton = ({ onClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <Button onClick={onClick} data-testid="v2-google-login"
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
              backgroundColor: hover ? "hsl(var(--login-btn-hover))" : "hsl(var(--login-btn))",
              color: "hsl(var(--login-btn-text))",
            }}
            className="w-full h-12 gap-3 rounded-xl text-base font-medium shadow-lg transition-colors">
      <GoogleIcon className="h-5 w-5" />
      Login dengan Google
    </Button>
  );
};

// ===== Login / Landing ETAMSINV (Fase 6D) — UI only, auth logic tidak diubah. =====
// PERBAIKAN REGRESI LOGIN (pasca S-H/S-I): LoginScreen SENGAJA TIDAK ikut tema
// aplikasi — satu rupa untuk semua pengguna, sama seperti label QR (.qr-print) yang
// wajib hitam-di-atas-putih apa pun tema layar. Semua warna di bawah memakai token
// --login-* (index.css, nilai IDENTIK di :root & .dark). NOL --primary/--muted/--accent/
// --primary-soft atau token bertema lain di sini, dan NOL nilai warna mentah (kecuali
// 4 hex resmi logo Google — pengecualian yang sah, lihat komentar GoogleIcon).
const LoginScreen = ({ login }) => (
  <div className="relative min-h-screen w-full overflow-hidden" style={{ backgroundColor: "hsl(var(--login-overlay))" }} data-testid="v2-login-screen">
    {/* Background full-cover (object-cover) — sporty/futuristic, tetap kuat.
        GANTI GAMBAR (permintaan Owner): login-bg.webp (layar lebar) & login-bg-portrait.webp
        (HP, viewport <=640px, breakpoint mobile standar). login-bg.png LAMA TIDAK dihapus,
        dibiarkan sbg cadangan di /public/brand/ (tidak dirujuk lagi di sini). */}
    <picture>
      <source media="(max-width: 640px)" srcSet="/brand/login-bg-portrait.webp" />
      <img src="/brand/login-bg.webp" alt="" aria-hidden="true"
           onError={(e) => { e.currentTarget.style.display = "none"; }}
           className="absolute inset-0 h-full w-full object-cover" />
    </picture>
    {/* PERBAIKAN (permintaan Owner): halaman depan TIDAK diadjust warnanya sama sekali.
        Empat lapisan gelap DIHAPUS (2 gradien overlay + 2 glow oranye/biru) supaya gambar
        latar tampil APA ADANYA — tanpa gradien, tanpa glow, tanpa peredupan. backgroundColor
        div terluar (hsl(var(--login-overlay))) DIBIARKAN sbg dasar di belakang gambar (tidak
        menutupi gambar). Kartu login tetap pakai backdrop-blur & latar semi-transparannya
        (itu bagian kartu, bukan penyesuaian gambar). LoginScreen tetap TIDAK ikut tema. */}

    {/* Logo ETAMS — brand utama, pojok kiri atas */}
    <div className="absolute left-6 top-6 z-20 sm:left-10 sm:top-8">
      <BrandLogo variant="white" className="h-9 sm:h-11 w-auto drop-shadow-lg" alt="ETAMS" />
    </div>

    <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      {/* SISI KIRI: klub + headline + subheadline (rata kiri, spacing lega) */}
      <div className="flex flex-col justify-center gap-8 px-6 pt-28 pb-8 sm:px-12 lg:px-20 lg:py-12">
        <div className="flex items-center gap-5" data-testid="v2-login-clubs">
          <SideLogo src="/brand/arema.png" alt="Arema FC" testid="v2-login-arema" />
          <SideLogo src="/brand/borneo.png" alt="Borneo FC" testid="v2-login-borneo" />
        </div>
        <div className="space-y-5">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white drop-shadow-sm"
              data-testid="v2-login-headline">ETAMSINV</h1>
          <p className="max-w-xl text-sm sm:text-base lg:text-lg leading-relaxed"
             style={{ color: "hsl(var(--login-text-soft) / 0.9)" }}
             data-testid="v2-login-subheadline">
            Satu platform untuk mengelola produksi, stok, transfer, pengiriman, penjualan, dan arsip dokumen ETAMS.
          </p>
        </div>
      </div>

      {/* SISI KANAN: card login Google (hanya logo ETAMS + Google + tombol) */}
      <div className="flex items-center justify-center px-6 pb-16 pt-2 sm:px-12 lg:px-12 lg:py-12">
        <div className="relative w-full max-w-md rounded-[28px] border border-white/15 p-10 shadow-[0_8px_40px_rgba(0,0,0,0.45)] backdrop-blur-2xl"
             style={{ backgroundColor: "hsl(var(--login-card) / 0.4)" }}
             data-testid="v2-login-card">
          <div className="absolute inset-x-0 -top-px mx-auto h-px w-2/3 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div className="flex flex-col items-center gap-9">
            {/* Logo ETAMS flame mark + logo Google berdampingan.
                Logo Google WAJIB di atas permukaan terang agar 4 lengannya (termasuk
                biru #1976D2) selalu terlihat, terlepas warna kartu di belakangnya. */}
            <div className="flex items-center gap-6" data-testid="v2-login-card-logos">
              <BrandMark variant="white" className="h-16 w-auto drop-shadow-lg" alt="ETAMS" />
              <span className="h-12 w-px bg-white/15" />
              <div className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: "hsl(var(--login-btn))" }}>
                <GoogleIcon className="h-9 w-9" />
              </div>
            </div>
            <GoogleLoginButton onClick={login} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

function Inner() {
  const { booting, session, me, meError, reloadMe, logout, login, uatMode } = useV2Auth();
  const location = useLocation();

  if (booting) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (!session) {
    // MASALAH TERPISAH (bukan dari S-I, sudah lama): setelah logout, alamat tetap
    // /dashboard karena V2App merender LoginScreen tanpa memindahkan alamat. Perbaikan
    // murni alamat/tampilan, TIDAK mengubah logika auth (login/logout/session tak disentuh):
    // begitu session kosong & alamat bukan root, luruskan lebih dulu secara deklaratif
    // (pola sama dgn redirect route lain di file ini), lalu render LoginScreen di "/".
    // Tidak memicu pengulangan navigasi: setelah alamat jadi "/", cabang ini tidak
    // tereksekusi lagi selama location.pathname tetap "/".
    if (location.pathname !== "/") return <Navigate to="/" replace />;
    return <LoginScreen login={login} />;
  }

  // B-8: /me GAGAL (jaringan/500) -> jangan spinner abadi; beri jalan keluar.
  if (meError) return (
    <div className="min-h-screen grid place-items-center p-6">
      <Card className="p-8 max-w-md w-full text-center space-y-4 rounded-2xl">
        <div className="flex justify-center"><BrandLogo variant="auto" className="h-10 w-auto" /></div>
        <h2 className="text-xl font-semibold">Gagal memuat profil Anda.</h2>
        <p className="text-sm text-muted-foreground" data-testid="v2-me-error-msg">
          Periksa koneksi Anda, lalu coba lagi.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button onClick={() => reloadMe()} data-testid="v2-me-retry-btn">Coba lagi</Button>
          <Button variant="outline" onClick={() => logout()} data-testid="v2-me-logout-btn">Keluar</Button>
        </div>
      </Card>
    </div>
  );

  if (!me) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (me.pending) return (
    <div className="min-h-screen grid place-items-center p-6">
      <Card className="p-8 max-w-md w-full text-center space-y-4 rounded-2xl">
        <div className="flex justify-center"><BrandLogo variant="auto" className="h-10 w-auto" /></div>
        <h2 className="text-xl font-semibold">Akses Belum Tersedia</h2>
        <p className="text-sm text-muted-foreground" data-testid="v2-not-registered-msg">
          Email Anda belum terdaftar di User Management ETAMSINV. Silakan hubungi Owner.
        </p>
      </Card>
    </div>
  );

  return (
    <V2RealtimeProvider>
    <Routes>
      <Route element={<V2Shell />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        {MENU.map((m) => {
          const Comp = READY[m.key] || (() => <Placeholder title={m.label} />);
          return (
            <Route key={m.key} path={m.path}
              element={<RoleGuard roles={m.roles} role={me.role}><PageBoundary resetKey={m.key}><Comp /></PageBoundary></RoleGuard>} />
          );
        })}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
    </V2RealtimeProvider>
  );
}

export default function V2App() {
  return <V2AuthProvider><Inner /></V2AuthProvider>;
}
